// "Ask AI": the ✨ button in the top bar of every dashboard page, and the panel it opens. The
// assistant answers questions and does things for the user; every change shows as a card with
// Confirm and Cancel. The panel stays open (with the same chat) when you go to another page.
import { api, el } from './format.js';

const OPEN_KEY = 'kv_assistant_open';
const CHAT_KEY = 'kv_assistant_chat';
const SUGGESTIONS = [
  'How do funnels count a step?',
  'How did my websites do this week?',
  'Create a draft A/B test for my pricing page',
  'Which tickets are waiting for an answer?',
];

const store = {
  get: (k) => {
    try {
      return sessionStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k, v) => {
    try {
      if (v === null) sessionStorage.removeItem(k);
      else sessionStorage.setItem(k, v);
    } catch {}
  },
};

let panel = null;
let state = { info: null, chat: null, busy: false, started: 0, error: '', files: [] };

const MAX_FILES = 4;
const MAX_IMAGE_EDGE = 1568; // larger pictures are shrunk before they are sent (what Claude reads anyway)
let ticker = null;

/** The ✨ Ask AI button for the top bar. */
export function assistantButton() {
  const b = el('button', { type: 'button', class: 'topbar-ai', title: 'Ask AI: questions about Kolvantage, and things to do for you', 'aria-expanded': 'false' },
    el('span', { 'aria-hidden': 'true' }, '✨'), el('span', { class: 'topbar-ai-label' }, 'Ask AI'));
  b.addEventListener('click', () => (panel && !panel.hidden ? close() : open()));
  if (store.get(OPEN_KEY) === '1') setTimeout(open);
  addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && panel && !panel.hidden && !state.busy) close();
  });
  return b;
}

const page = () => ({ path: location.pathname + location.search + location.hash, title: document.title });

async function open() {
  if (!panel) build();
  panel.hidden = false;
  document.body.classList.add('ai-open');
  document.querySelector('.topbar-ai')?.setAttribute('aria-expanded', 'true');
  store.set(OPEN_KEY, '1');
  try {
    state.info = await api('/assistant');
    const saved = Number(store.get(CHAT_KEY));
    if (saved && !state.chat && state.info.chats.some((c) => c.id === saved)) state.chat = await api(`/assistant/chats/${saved}`);
  } catch (err) {
    state.error = err.message;
  }
  render();
  panel.querySelector('textarea')?.focus();
}

function close() {
  if (!panel) return;
  panel.hidden = true;
  document.body.classList.remove('ai-open');
  document.querySelector('.topbar-ai')?.setAttribute('aria-expanded', 'false');
  store.set(OPEN_KEY, null);
}

function build() {
  panel = el('aside', { class: 'ai-panel', 'aria-label': 'Ask AI', hidden: '' });
  // Files dropped on the panel are attached.
  panel.addEventListener('dragover', (e) => {
    if ([...(e.dataTransfer?.types || [])].includes('Files')) e.preventDefault();
  });
  panel.addEventListener('drop', (e) => {
    if (!e.dataTransfer?.files?.length) return;
    e.preventDefault();
    if (!state.busy && state.info?.available) addFiles(e.dataTransfer.files);
  });
  document.body.append(panel);
}

function newChat() {
  state.chat = null;
  state.error = '';
  store.set(CHAT_KEY, null);
  render();
  panel.querySelector('textarea')?.focus();
}

async function openChat(id) {
  try {
    state.chat = await api(`/assistant/chats/${id}`);
    store.set(CHAT_KEY, String(id));
    state.error = '';
  } catch (err) {
    state.error = err.message;
  }
  render();
}

// One request at a time; the panel shows how long it's been thinking.
async function busy(fn) {
  state.busy = true;
  state.started = Date.now();
  state.error = '';
  render();
  ticker = setInterval(() => {
    const t = panel.querySelector('.ai-thinking-time');
    if (t) t.textContent = `${Math.round((Date.now() - state.started) / 1000)} s`;
  }, 1000);
  try {
    await fn();
  } catch (err) {
    state.error = err.message;
  } finally {
    clearInterval(ticker);
    state.busy = false;
    render();
  }
}

function send(text) {
  const message = text.trim();
  const files = state.files;
  if ((!message && !files.length) || state.busy || !state.info?.available) return;
  // Shown right away; the answer replaces the whole chat.
  const item = { role: 'user', text: message || 'Please look at the attached file.', ...(files.length ? { attachments: files.map((f) => ({ name: f.name, kind: f.kind, size: f.size, preview: f.preview })) } : {}) };
  const shown = state.chat ? { ...state.chat, items: [...state.chat.items, item] } : { id: null, items: [item] };
  const attachments = files.map((f) => ({ name: f.name, type: f.type, data: f.data }));
  state.files = [];
  const current = state.chat;
  state.chat = shown;
  busy(async () => {
    try {
      state.chat = current
        ? await api(`/assistant/chats/${current.id}/messages`, { method: 'POST', body: { message, page: page(), attachments } })
        : await api('/assistant/chats', { method: 'POST', body: { message, page: page(), attachments } });
      store.set(CHAT_KEY, String(state.chat.id));
      state.info = await api('/assistant').catch(() => state.info);
    } catch (err) {
      state.chat = current;
      state.files = files; // nothing was sent: they can try again
      throw err;
    }
  });
}

// ------------------------------------------------------------------ attached files

const readAsBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.onerror = () => reject(new Error('The file could not be read'));
  r.readAsDataURL(blob);
});
const sizeText = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** A picture, shrunk when it is large (screens are often 3000 px wide), as a JPEG or PNG blob. */
async function shrink(file) {
  if (file.type === 'image/gif' || !('createImageBitmap' in window)) return file;
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // not something the browser can draw: the server decides
  }
  const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && file.size <= 1048576) return file;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff'; // a transparent picture on white, as JPEG has no transparency
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  return blob && blob.size < file.size ? new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }) : file;
}

/** Adds pictures and files picked, dropped or pasted into the message. */
async function addFiles(list) {
  const incoming = [...list];
  if (!incoming.length) return;
  state.error = '';
  for (const raw of incoming) {
    if (state.files.length >= MAX_FILES) {
      state.error = `Up to ${MAX_FILES} files in one message`;
      break;
    }
    try {
      const isImage = /^image\//.test(raw.type);
      const file = isImage ? await shrink(raw) : raw;
      if (!isImage && raw.size > 8 * 1024 * 1024) throw new Error(`“${raw.name}” is too big (8 MB at most)`);
      const name = raw.name && !/^image\.\w+$/.test(raw.name) ? raw.name : `${isImage ? 'screenshot' : 'file'}-${new Date().toISOString().slice(11, 19).replace(/:/g, '')}${file.type === 'image/jpeg' ? '.jpg' : '.png'}`;
      const data = await readAsBase64(file);
      state.files.push({ name, type: file.type, data, size: file.size, kind: isImage ? 'image' : file.type === 'application/pdf' ? 'pdf' : 'text', preview: isImage ? `data:${file.type};base64,${data}` : null });
    } catch (err) {
      state.error = err.message;
    }
  }
  render();
}

function decide(actionId, decision) {
  const chat = state.chat;
  busy(async () => {
    state.chat = await api(`/assistant/chats/${chat.id}/actions/${actionId}`, { method: 'POST', body: { decision, page: page() } });
  });
}

async function removeChat() {
  if (!state.chat?.id || !confirm('Delete this chat?')) return;
  await api(`/assistant/chats/${state.chat.id}`, { method: 'DELETE' }).catch(() => {});
  state.info = await api('/assistant').catch(() => state.info);
  newChat();
}

// ------------------------------------------------------------------ drawing

function render() {
  if (!panel) return;
  const info = state.info;
  const chats = info?.chats || [];
  const picker = el('select', { class: 'ai-chats', 'aria-label': 'Your chats with Ask AI' },
    el('option', { value: '' }, state.chat?.id ? 'Earlier chats…' : 'New chat'),
    ...chats.map((c) => el('option', { value: String(c.id), selected: state.chat?.id === c.id ? '' : null }, c.title || `Chat ${c.id}`)));
  picker.addEventListener('change', () => (picker.value ? openChat(Number(picker.value)) : newChat()));
  const head = el('div', { class: 'ai-head' },
    el('strong', { class: 'ai-title' }, '✨ Ask AI'),
    chats.length ? picker : null,
    el('button', { type: 'button', class: 'btn-small', onclick: newChat, disabled: state.busy ? '' : null, title: 'Start a new chat' }, 'New'),
    state.chat?.id ? el('button', { type: 'button', class: 'btn-small', onclick: removeChat, disabled: state.busy ? '' : null, title: 'Delete this chat', 'aria-label': 'Delete this chat' }, '🗑') : null,
    el('button', { type: 'button', class: 'ai-x', onclick: close, 'aria-label': 'Close' }, '×'));

  const body = el('div', { class: 'ai-body', 'aria-live': 'polite' });
  if (!info && !state.error) body.append(el('p', { class: 'muted' }, 'Loading…'));
  else if (info && !info.available) {
    body.append(el('div', { class: 'ai-empty' }, el('p', {}, info.reason),
      info.canManage ? el('a', { class: 'btn btn-small', href: 'account.html#assistant' }, 'Set up Ask AI') : null));
  } else if (!state.chat?.items.length) {
    body.append(el('div', { class: 'ai-empty' },
      el('p', {}, el('strong', {}, 'Ask me anything about Kolvantage, or tell me what to do.')),
      el('p', { class: 'muted small' }, 'I can see and do what you can in the dashboard. Before I change anything, you get a card to confirm.'),
      el('div', { class: 'ai-suggest' }, ...SUGGESTIONS.map((s) => el('button', { type: 'button', class: 'ai-chip', onclick: () => send(s) }, s)))));
  } else {
    for (const item of state.chat.items) body.append(itemEl(item));
  }
  if (state.busy) body.append(el('div', { class: 'ai-thinking' }, el('span', { class: 'ai-dots' }, el('i'), el('i'), el('i')), 'Working on it… ', el('span', { class: 'ai-thinking-time muted' }, '0 s')));
  if (state.error) body.append(el('p', { class: 'ai-error', role: 'alert' }, state.error));

  const box = el('textarea', { rows: '2', placeholder: state.chat?.pending ? 'Or write something else instead…' : 'Ask a question or say what to do…', 'aria-label': 'Message to Ask AI', maxlength: '4000', disabled: state.busy || !info?.available ? '' : null });
  box.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      send(box.value);
    }
  });
  box.addEventListener('paste', (e) => {
    const files = [...(e.clipboardData?.files || [])];
    if (files.length) {
      e.preventDefault(); // a pasted screenshot or file becomes an attachment (pasted text stays text)
      addFiles(files);
    }
  });
  const picker2 = el('input', { type: 'file', multiple: '', hidden: '', accept: 'image/png,image/jpeg,image/gif,image/webp,application/pdf,.txt,.md,.csv,.tsv,.json,.log,.html,.xml,.yml,.yaml,.js,.css,.sql' });
  picker2.addEventListener('change', () => {
    addFiles(picker2.files);
    picker2.value = '';
  });
  const attach = el('button', { type: 'button', class: 'btn btn-small ai-attach', title: 'Attach a picture, PDF or text file (you can also paste or drop a screenshot)', 'aria-label': 'Attach a file', disabled: state.busy || !info?.available ? '' : null, onclick: () => picker2.click() }, '📎');
  const chips = state.files.length
    ? el('div', { class: 'ai-files' }, ...state.files.map((f, i) => el('span', { class: 'ai-file' },
        f.preview ? el('img', { src: f.preview, alt: '', class: 'ai-thumb' }) : el('span', { 'aria-hidden': 'true' }, f.kind === 'pdf' ? '📄' : '📝'),
        el('span', { class: 'ai-file-name', title: f.name }, f.name), el('span', { class: 'muted small' }, sizeText(f.size)),
        el('button', { type: 'button', 'aria-label': `Remove ${f.name}`, onclick: () => ((state.files = state.files.filter((_, j) => j !== i)), render()) }, '×'))))
    : null;
  const foot = el('form', { class: 'ai-foot', onsubmit: (e) => (e.preventDefault(), send(box.value)) },
    attach, picker2, box, el('button', { type: 'submit', class: 'btn btn-primary btn-small', disabled: state.busy || !info?.available ? '' : null }, 'Send'));

  const draft = panel.querySelector('textarea')?.value || '';
  panel.replaceChildren(head, body, ...(chips ? [chips] : []), foot);
  if (draft && !state.busy) box.value = draft;
  body.scrollTop = body.scrollHeight;
  if (!state.busy && info?.available) box.focus();
}

function itemEl(item) {
  if (item.role === 'user') {
    return el('div', { class: 'ai-msg ai-user' }, item.text,
      item.attachments?.length ? el('div', { class: 'ai-sent-files' }, ...item.attachments.map((a) => (a.preview ? el('img', { src: a.preview, alt: a.name, title: a.name, class: 'ai-thumb big' }) : el('span', { class: 'ai-file sent' }, `${{ image: '🖼', pdf: '📄', text: '📝' }[a.kind] || '📎'} ${a.name}`)))) : null);
  }
  if (item.role === 'assistant') return el('div', { class: 'ai-msg ai-bot' }, ...markdown(item.text));
  if (item.role === 'step') return el('div', { class: 'ai-step' }, `🔎 ${item.text}`);
  if (item.role === 'error') return el('p', { class: 'ai-error' }, item.text);
  if (item.role === 'action') return actionEl(item);
  return el('div');
}

const STATUS = { done: '✓ Done', failed: '✕ Didn’t work', cancelled: 'Cancelled', pending: '' };

function actionEl(a) {
  const pending = a.status === 'pending' && state.chat?.pending === a.actionId;
  return el('div', { class: `ai-action ${a.risk === 'risky' ? 'risky' : ''} ${a.status}` },
    el('div', { class: 'ai-action-head' }, a.risk === 'risky' ? '⚠️ Please check: this can’t be undone easily or reaches people' : 'Ask AI wants to make a change'),
    el('p', { class: 'ai-action-summary' }, a.summary),
    el('code', { class: 'ai-action-call' }, `${a.method} ${a.path}`),
    pending
      ? el('div', { class: 'ai-action-buttons' },
          el('button', { type: 'button', class: `btn btn-small ${a.risk === 'risky' ? 'btn-danger' : 'btn-primary'}`, disabled: state.busy ? '' : null, onclick: () => decide(a.actionId, 'confirm') }, 'Confirm'),
          el('button', { type: 'button', class: 'btn btn-small', disabled: state.busy ? '' : null, onclick: () => decide(a.actionId, 'cancel') }, 'Cancel'))
      : el('div', { class: `ai-action-status ${a.status}` }, STATUS[a.status] || a.status, a.result ? el('span', { class: 'muted small' }, ` ${a.result.split('\n')[0]}`) : null));
}

// ------------------------------------------------------------------ a little Markdown (safe: built as elements)

/** Links: dashboard pages open here; the docs and other https pages in a new tab; anything else is text. */
function link(label, href) {
  if (/^\/dashboard\//.test(href) || /^[\w-]+\.html(\?|#|$)/.test(href)) return el('a', { href }, label);
  if (/^https:\/\//.test(href)) return el('a', { href, target: '_blank', rel: 'noopener noreferrer' }, label);
  return document.createTextNode(label);
}

function inline(text) {
  const out = [];
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)|https:\/\/[^\s<>()]+[^\s<>().,;:!?])/g;
  let last = 0;
  let m;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(document.createTextNode(text.slice(last, m.index)));
    if (m[2]) out.push(el('strong', {}, m[2]));
    else if (m[3]) out.push(el('code', {}, m[3]));
    else if (m[4]) out.push(link(m[4], m[5]));
    else out.push(link(m[0], m[0]));
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(document.createTextNode(text.slice(last)));
  return out;
}

export function markdown(text) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let list = null;
  let para = [];
  const flushPara = () => {
    if (para.length) out.push(el('p', {}, ...inline(para.join(' '))));
    para = [];
  };
  const flushList = () => {
    if (list) out.push(list);
    list = null;
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('```')) {
      flushPara();
      flushList();
      const code = [];
      for (i++; i < lines.length && !lines[i].startsWith('```'); i++) code.push(lines[i]);
      out.push(el('pre', {}, el('code', {}, code.join('\n'))));
      continue;
    }
    const item = /^\s*([-*]|\d+[.)])\s+(.*)$/.exec(line);
    if (item) {
      flushPara();
      const ordered = /\d/.test(item[1]);
      if (!list || (list.tagName === 'OL') !== ordered) {
        flushList();
        list = el(ordered ? 'ol' : 'ul');
      }
      list.append(el('li', {}, ...inline(item[2])));
      continue;
    }
    const h = /^#{1,4}\s+(.*)$/.exec(line);
    if (h) {
      flushPara();
      flushList();
      out.push(el('p', { class: 'ai-h' }, el('strong', {}, ...inline(h[1]))));
      continue;
    }
    if (!line.trim()) {
      flushPara();
      flushList();
      continue;
    }
    if (list && /^\s{2,}/.test(line)) {
      list.lastChild.append(' ', ...inline(line.trim()));
      continue;
    }
    flushList();
    para.push(line.trim());
  }
  flushPara();
  flushList();
  return out;
}
